var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Every Firebase call the sign-in panel makes. The UI components import from here
 * and never touch the SDK, so if the SDK surface moves there is one file to fix.
 *
 * On the compat/modular split: the app is built on the compat shim
 * (src/components/FirebaseAuth/firebase.ts), and this file keeps it that way for
 * everything except one thing — TotpMultiFactorGenerator is not registered on the
 * compat auth namespace, only in modular `firebase/auth`.
 *
 * Mixing is safe here, and specifically:
 *   - TotpMultiFactorGenerator and FactorId are inert. The generator has only
 *     static methods and FactorId is a frozen object, so neither touches an Auth
 *     instance and there is no dual-instance hazard.
 *   - The compat layer attaches `error.resolver` to every
 *     auth/multi-factor-auth-required error, and that resolver wraps the modular
 *     one, so it accepts a modular assertion unchanged.
 *   - The app already mixes this way: src/components/StyledFirebaseAuth imports
 *     modular onAuthStateChanged against the compat instance.
 *
 * Sign-in stays on compat's signInWithPopup rather than the modular one, because
 * compat supplies its own popup/redirect resolver explicitly; calling modular
 * would quietly depend on a default resolver being installed at init.
 */
import firebase from "firebase/compat/app";
import 'firebase/compat/auth';
import { TotpMultiFactorGenerator, FactorId } from 'firebase/auth';
import { FirebaseAuth } from '../FirebaseAuth/firebase';
var auth = function () { return FirebaseAuth.auth(); };
/**
 * FirebaseUI set this by default. Without it a visitor with several Google
 * accounts is silently signed in as whichever one the browser saw last, with no
 * chance to pick — a regression that is easy to miss because it only shows up for
 * multi-account users.
 */
var withAccountPrompt = function (provider) {
    provider.setCustomParameters({ prompt: 'select_account' });
    return provider;
};
export var signInWithPassword = function (email, password) {
    return auth().signInWithEmailAndPassword(email, password);
};
/**
 * Creates a password account.
 *
 * Order matters: updateProfile must land BEFORE the caller runs userSignIn(),
 * because src/libs/user writes `displayName: currentUser.displayName` into
 * users/{uid}. Getting this backwards stores null for every new password user and
 * nothing would ever correct it. FirebaseUI handled this for us.
 *
 * The verification email is best-effort — a mail failure must not fail sign-up.
 * /user/profile/verify-email exists to retry.
 */
export var createEmailAccount = function (email, password, displayName) { return __awaiter(void 0, void 0, void 0, function () {
    var credential, user, name, e_1;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, auth().createUserWithEmailAndPassword(email, password)];
            case 1:
                credential = _a.sent();
                user = credential.user;
                if (!user) return [3 /*break*/, 6];
                name = displayName.trim();
                if (!(name !== '')) return [3 /*break*/, 3];
                return [4 /*yield*/, user.updateProfile({ displayName: name })];
            case 2:
                _a.sent();
                _a.label = 3;
            case 3:
                _a.trys.push([3, 5, , 6]);
                return [4 /*yield*/, user.sendEmailVerification()];
            case 4:
                _a.sent();
                return [3 /*break*/, 6];
            case 5:
                e_1 = _a.sent();
                return [3 /*break*/, 6];
            case 6: return [2 /*return*/, credential];
        }
    });
}); };
export var signInWithGoogle = function () {
    return auth().signInWithPopup(withAccountPrompt(new firebase.auth.GoogleAuthProvider()));
};
export var signInWithMicrosoft = function () {
    return auth().signInWithPopup(withAccountPrompt(new firebase.auth.OAuthProvider('microsoft.com')));
};
export var sendReset = function (email) { return auth().sendPasswordResetEmail(email); };
/** True for a second factor this app can actually challenge. */
export var isTotpHint = function (hint) {
    return hint.factorId === FactorId.TOTP;
};
/**
 * Completes a sign-in that was interrupted by a TOTP challenge.
 *
 * The assertion is built by the modular SDK and handed to a compat resolver. That
 * works because compat's resolveSignIn forwards the object straight through to the
 * modular resolver it was constructed from — no unwrapping, no instanceof check.
 * The cast bridges two structurally-identical declarations of the same shape.
 *
 * On success the SDK sets currentUser before resolving, so the caller can proceed
 * into the normal post-sign-in path.
 */
export var resolveTotp = function (resolver, hintUid, code) {
    var assertion = TotpMultiFactorGenerator.assertionForSignIn(hintUid, code);
    return resolver.resolveSignIn(assertion);
};
/**
 * Links a credential stashed from auth/account-exists-with-different-credential,
 * after the visitor has proved ownership by signing in with their original method.
 * Already-linked is not an error worth surfacing.
 */
export var linkPendingCredential = function (credential) { return __awaiter(void 0, void 0, void 0, function () {
    var user, e_2;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                user = auth().currentUser;
                if (!user) {
                    return [2 /*return*/];
                }
                _a.label = 1;
            case 1:
                _a.trys.push([1, 3, , 4]);
                return [4 /*yield*/, user.linkWithCredential(credential)];
            case 2:
                _a.sent();
                return [3 /*break*/, 4];
            case 3:
                e_2 = _a.sent();
                if ((e_2 === null || e_2 === void 0 ? void 0 : e_2.code) !== 'auth/provider-already-linked' && (e_2 === null || e_2 === void 0 ? void 0 : e_2.code) !== 'auth/credential-already-in-use') {
                    throw e_2;
                }
                return [3 /*break*/, 4];
            case 4: return [2 /*return*/];
        }
    });
}); };
